<template>
  <div class="deliverable-assets-management">
    <v-container fluid :class="embedded ? 'pa-0' : 'pa-6'">
      <v-card outlined class="deliverables-card">
        <!-- The public container supplies its own breadcrumb/header. -->
        <div v-if="!publicMode" class="card-header pa-4 d-flex align-center">
          <v-divider class="custom-vertical-divider ml-1 mr-2" vertical></v-divider>

          <div class="flex-grow-1">
            <div class="text-subtitle-1 font-weight-medium">
              <v-icon class="mr-3">mdi-image-multiple-outline</v-icon>{{ $t('deliverable_assets') }}
            </div>
            <div class="text-caption grey--text">
              {{ baseInstances.length }} {{ $t('deliverable_singular') }} ·
              {{ totalAssets }} {{ $t('markup.assets') }}
            </div>
          </div>

          <v-btn text class="mr-2" @click="toggleAll" small>
            <v-icon left>{{ allDeliverablesExpanded ? 'mdi-unfold-less-horizontal' : 'mdi-unfold-more-horizontal' }}</v-icon>
            {{ allDeliverablesExpanded ? $t('close_all') : $t('open_all') }}
          </v-btn>

          <v-btn-toggle v-if="!publicMode" mandatory dense value="assets" class="ml-2 deliverable-nav-toggle">
            <v-btn value="assets-list" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')" @click="openDeliverableAssetsListView">
              <v-icon>mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn value="deliverables" icon :title="$t('deliverables_list')" :aria-label="$t('deliverables_list')" @click="openDeliverableManagementView">
              <v-icon>mdi-format-list-text</v-icon>
            </v-btn>
            <v-btn value="assets" color="primary" icon :title="$t('deliverable_assets')" :aria-label="$t('deliverable_assets')">
              <v-icon color="primary">mdi-list-status</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>

        <div class="pa-4">
          <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

          <div v-if="baseInstances.length === 0 && !loading" class="text-center py-12 grey--text">
            <v-icon large color="grey lighten-1">mdi-package-variant-closed</v-icon>
            <div class="mt-2">{{ $t('no_deliverables_added') }}</div>
          </div>

          <!-- Deliverable Items -->
          <v-card
            v-for="(baseInstance, index) in baseInstances"
            :key="baseInstance.base_instance_id"
            class="deliverable-item mb-4"
            outlined
          >
            <div class="deliverable-header">
              <div class="d-flex align-center pa-3 deliverable-header-row">
                <v-btn icon @click="toggleDeliverable(baseInstance)" class="mr-2">
                  <v-icon>
                    {{ expandedDeliverables[baseInstance.base_instance_id] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                  </v-icon>
                </v-btn>

                <v-chip color="primary lighten-2" class="mr-3 font-weight-bold black--text" label>
                  #{{ index + 1 }}
                </v-chip>

                <v-icon class="mr-2">{{ getBaseInstanceIcon(baseInstance) }}</v-icon>

                <span class="font-weight-medium mr-2">{{ baseInstance.base_title }}</span>

                <v-spacer></v-spacer>

                <v-chip outlined class="mr-2">
                  {{ getStatusTitle(baseInstance.status_id) }}
                </v-chip>
                <v-chip outlined>
                  <v-icon left small>mdi-image-multiple-outline</v-icon>
                  {{ getDeliverableAssets(baseInstance).length }} {{ $t('markup.assets') }}
                </v-chip>
              </div>

              <v-expand-transition>
                <div v-show="expandedDeliverables[baseInstance.base_instance_id]" class="px-4 pb-4">

                  <v-progress-linear
                    v-if="loadingAssets[baseInstance.base_instance_id]"
                    indeterminate
                    color="primary"
                    class="mb-3"
                  ></v-progress-linear>

                  <div
                    v-else-if="getDeliverableAssets(baseInstance).length === 0"
                    class="text-center py-6 grey--text text--lighten-1"
                  >
                    <v-icon color="grey lighten-1">mdi-image-off-outline</v-icon>
                    <div class="mt-2">{{ $t('no_assets_in_deliverable') }}</div>
                  </div>

                  <div v-else class="assets-grid">
                    <div
                      v-for="asset in getDeliverableAssets(baseInstance)"
                      :key="asset.job_deliver_asset_id"
                      class="asset-card"
                    >
                      <v-card outlined class="asset-card-inner">
                        <!-- Thumbnail -->
                        <div
                          class="asset-thumbnail"
                          style="cursor: pointer;"
                          :style="{ aspectRatio: getAssetAspectRatio(asset) }"
                          @click="openAssetReview(asset)"
                        >
                          <v-img
                            v-if="getDisplayedThumbnail(asset)"
                            :src="getDisplayedThumbnail(asset)"
                            class="grey lighten-2 fill-height"
                            contain
                          >
                            <template v-slot:placeholder>
                              <v-row class="fill-height ma-0" align="center" justify="center">
                                <v-progress-circular indeterminate color="grey lighten-1"></v-progress-circular>
                              </v-row>
                            </template>
                          </v-img>
                          <div v-else class="asset-thumbnail-placeholder">
                            <v-icon large color="grey lighten-1">mdi-file-outline</v-icon>
                          </div>

                          <!-- Status Badge -->
                          <div class="asset-status-badge">
                            <v-chip v-if="getDisplayedStatus(asset) === 'rejected'" small color="error" label class="px-1">
                              <v-icon small>mdi-close-thick</v-icon>
                            </v-chip>
                            <v-chip v-else-if="getDisplayedStatus(asset) === 'approved'" small color="success" label class="px-1">
                              <v-icon small>mdi-check-bold</v-icon>
                            </v-chip>
                            <v-chip v-else-if="getDisplayedStatus(asset) === 'approved_finalize'" small color="warning" label class="px-1">
                              <v-icon small>mdi-check-bold</v-icon>
                            </v-chip>
                            <v-chip v-else-if="getDisplayedStatus(asset) === 'pending'" small label class="px-1">
                              <v-icon small>mdi-clock-outline</v-icon>
                            </v-chip>
                          </div>
                        </div>

                        <!-- Hover Menu -->
                        <div class="asset-actions" @click.stop>
                          <v-menu offset-y left>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn x-small fab v-bind="attrs" v-on="on" @click.stop>
                                <v-icon small>mdi-dots-vertical</v-icon>
                              </v-btn>
                            </template>
                            <v-list dense>
                              <v-list-item @click="openAssetReview(asset)">
                                <v-list-item-icon class="mr-2">
                                  <v-icon>mdi-list-status</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('markup.approval') }}</v-list-item-title>
                              </v-list-item>
                              <v-list-item @click="downloadAsset(asset)">
                                <v-list-item-icon class="mr-2">
                                  <v-icon>mdi-download-outline</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>{{ $t('download') }}</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </div>
                      </v-card>

                      <!-- Asset Title Below Card -->
                      <div class="asset-title mt-2 px-1">
                        <div class="text-caption grey--text text-truncate" :title="asset.formatLabel">
                          {{ asset.formatLabel }}
                        </div>

                        <v-tooltip
                          bottom
                          eager
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              v-bind="attrs"
                              v-on="on"
                              class="text-caption text-truncate"
                            >{{ asset.name || $t('markup.unnamed_asset') }}
                            </div>
                          </template>

                          <span>{{ asset.name || $t('markup.unnamed_asset') }}</span>
                        </v-tooltip>
                      </div>

                      <!-- Version Navigator -->
                      <div class="asset-version-nav mt-1">
                        <v-btn
                          small
                          icon
                          :disabled="!canGoToPreviousVersion(asset)"
                          @click="goToPreviousVersion(asset)"
                        >
                          <v-icon>mdi-chevron-left</v-icon>
                        </v-btn>

                        <v-chip
                          v-for="node in getVisibleVersionNodes(asset)"
                          :key="node.node_id"
                          small
                          class="px-2 mx-1 black--text"
                          :outlined="node.node_id !== getSelectedNode(asset).node_id"
                          :color="node.node_id === getSelectedNode(asset).node_id ? 'primary lighten-2' : ''"
                          @click="selectVersion(asset, node)"
                          label
                        >
                          v{{ node.version }}
                        </v-chip>

                        <v-btn
                          small
                          icon
                          :disabled="!canGoToNextVersion(asset)"
                          @click="goToNextVersion(asset)"
                        >
                          <v-icon>mdi-chevron-right</v-icon>
                        </v-btn>
                      </div>
                    </div>
                  </div>
                </div>
              </v-expand-transition>
            </div>
          </v-card>
        </div>
      </v-card>
    </v-container>

    <!-- Markup Session Dialog -->
    <v-dialog v-model="markupSessionDialog" max-width="90vw" max-height="90vh" @input="onMarkupDialogChange">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span>{{ selectedAssetForReview?.name || $t('markup.asset_review') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeMarkupSessionDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0" style="height: 80vh;">
          <MarkupSession
            v-if="selectedAssetForReview?.markup_session_id"
            :sessionId="selectedAssetForReview.markup_session_id"
            :hideToolbar="true"
            :canEditMarkup="canEditMarkup"
            :can-create-markup="canCreateMarkup"
            :hideApproval="publicMode"
            :useJobApprovalVideoPlayer="true"
            @status-updated="handleStatusUpdate"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import AppActions from '@/store/app/actions-types'
import ContentActions from '@/store/content/actions/actions-types'
import JobAPI from '@/services/api/job/job'
import JobDeliverableAPI from '@/services/api/job/deliverable'
import JobDeliverAssetAPI from '@/services/api/job/job_deliver_asset'
import DeliverableAPI from '@/services/api/business/deliverable'
import StatusAPI from '@/services/api/business/status'
import LaneAPI from '@/services/api/job/lane'
import PoolACLAPI from '@/services/api/job/pool_acl'
import MarkupSession from '@/views/content/markup/MarkupSession.vue'

export default {
  name: 'DeliverableAssetsManagement',

  components: {
    MarkupSession
  },

  props: {
    // Job ID - when not informed the route param is used
    jobId: {
      type: [String, Number],
      default: null
    },
    // Job data object - avoids reloading the job when the parent already has it
    jobData: {
      type: Object,
      default: () => ({})
    },
    // Customer ID - required for loading deliverables and statuses
    customerId: {
      type: [String, Number],
      default: null
    },
    // Whether the component is embedded in another component (like Job.vue)
    embedded: {
      type: Boolean,
      default: false
    },
    publicMode: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      // ACL bit for markup editing (same value used in JobDeliverableManagement)
      ACL_EDIT_MARKUP: 512,

      loading: false,
      localJobData: {},
      baseInstances: [],
      deliverableOptions: [],
      statusOptions: [],
      jobAcl: 0,

      expandedDeliverables: {},
      loadingAssets: {},
      // Assets indexed by base_instance_id
      assetsByBaseInstance: {},

      markupSessionDialog: false,
      selectedAssetForReview: null,

      // Detected "width / height" aspect ratio for each asset thumbnail, keyed by job_deliver_asset_id
      assetAspectRatios: {},

      // Currently selected node (version) id for each asset card, keyed by job_deliver_asset_id
      selectedVersionByAsset: {}
    }
  },

  computed: {
    effectiveJobId() {
      return this.jobId || this.$route.params.jobId || null
    },

    effectiveCustomerId() {
      return this.customerId || this.localJobData?.customer_id || this.jobData?.customer_id || null
    },

    canEditMarkup() {
      if (this.publicMode) return false
      return !this.isJobReadonly && (this.jobAcl & this.ACL_EDIT_MARKUP) === this.ACL_EDIT_MARKUP
    },

    canCreateMarkup() {
      return !this.isJobReadonly && (this.jobAcl & this.ACL_EDIT_MARKUP) === this.ACL_EDIT_MARKUP
    },

    isJobReadonly() {
      return !!(this.localJobData?.readonly || this.jobData?.readonly)
    },

    totalAssets() {
      return this.baseInstances.reduce((sum, base) => {
        return sum + (this.assetsByBaseInstance[base.base_instance_id]?.length || 0)
      }, 0)
    },

    allDeliverablesExpanded() {
      return this.baseInstances.length > 0 &&
        this.baseInstances.every(base => this.expandedDeliverables[base.base_instance_id])
    }
  },

  watch: {
    allDeliverablesExpanded(value) {
      this.$emit('expanded-change', value)
    },
    jobData: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.localJobData = { ...this.localJobData, ...newVal }
        }
      }
    }
  },

  mounted() {
    this.initialize()
  },

  methods: {
    ...mapActions('app', [
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),
    ...mapActions('content/actions', [
      ContentActions.DOWNLOAD_FILE
    ]),

    async initialize() {
      this.loading = true

      try {
        this.localJobData = { ...this.jobData }

        if (!this.effectiveJobId) {
          return
        }

        await this.loadJobDataFromAPI()

        await Promise.all([
          this.loadDeliverables(),
          this.loadStatuses(),
          this.loadJobAcl()
        ])

        await this.loadBaseInstances()
        await this.expandAllDeliverables()
      } catch (error) {
        console.error('Error initializing deliverable assets:', error)
      } finally {
        this.loading = false
      }
    },

    openDeliverableManagementView() {
      if (this.embedded) {
        this.$emit('open-deliverable-management')
        return
      }

      this.$router.push({
        name: 'JobDeliverableManagement',
        params: {
          appId: this.$route.params.appId,
          jobId: this.effectiveJobId,
          contentAppId: this.$route.params.contentAppId,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    openDeliverableAssetsListView() {
      if (this.embedded) {
        this.$emit('open-deliverable-assets-list')
        return
      }

      this.$router.push({
        name: 'JobDeliverableAssetsList',
        params: {
          appId: this.$route.params.appId,
          jobId: this.effectiveJobId,
          contentAppId: this.$route.params.contentAppId,
          businessAppId: this.$route.params.businessAppId
        }
      })
    },

    async loadJobDataFromAPI() {
      // The parent may already have provided the job data
      if (this.localJobData?.job_id) return

      try {
        const jobResponse = await JobAPI.getJob(this.effectiveJobId)
        this.localJobData = { ...this.localJobData, ...jobResponse.data }
      } catch (error) {
        console.error('Error loading job data:', error)
      }
    },

    async loadDeliverables() {
      if (!this.effectiveCustomerId) return

      try {
        const response = await DeliverableAPI.getAllDeliverablesByCustomer(this.effectiveCustomerId, {
          order: 'deliverable_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.deliverableOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading deliverables:', error)
      }
    },

    async loadStatuses() {
      if (!this.effectiveCustomerId) return

      try {
        const response = await StatusAPI.getAllStatusesByCustomer(this.effectiveCustomerId, {
          order: 'status_title',
          direction: 'ASC',
          offset: 0,
          limit: 1000
        })
        this.statusOptions = response.data.items || []
      } catch (error) {
        console.error('Error loading statuses:', error)
      }
    },

    async loadJobAcl() {
      const laneId = this.localJobData?.lane_id

      if (!laneId) {
        this.jobAcl = 0
        return
      }

      try {
        const laneResponse = await LaneAPI.getLane(laneId)
        const poolId = laneResponse.data?.pool_id

        if (!poolId) {
          this.jobAcl = 0
          return
        }

        const result = await PoolACLAPI.getPoolACLsFromUserGroup()
        const allPoolAcls = result.data.items || []

        this.jobAcl = allPoolAcls
          .filter(acl => acl.pool_id == poolId)
          .reduce((combined, acl) => combined | (parseInt(acl.acl) || 0), 0)
      } catch (error) {
        console.error('Error loading job ACL:', error)
        this.jobAcl = 0
      }
    },

    async loadBaseInstances() {
      if (!this.effectiveJobId) return

      try {
        const response = await JobDeliverableAPI.getJobDeliverables(this.effectiveJobId)

        let items = []
        if (Array.isArray(response.data)) {
          items = response.data
        } else if (response.data && Array.isArray(response.data.items)) {
          items = response.data.items
        } else if (response.data) {
          items = [response.data]
        }

        this.baseInstances = items
          .filter(item => item && item.base_instance)
          .map(item => {
          const baseInstance = { ...item.base_instance }

          baseInstance.formatInstances = (item.format_instances || []).map(formatItem => {
            const formatInstance = { ...formatItem.format_instance }

            // Map 'format' from API to 'format_desc' for component use
            if (formatInstance.format !== undefined) {
              formatInstance.format_desc = formatInstance.format
              delete formatInstance.format
            }

            formatInstance.job_deliver = formatItem.job_deliver || null

            return formatInstance
          })

          return baseInstance
        })
      } catch (error) {
        console.error('Error loading base instances:', error)
      }
    },

    getDeliverableAssets(baseInstance) {
      return this.assetsByBaseInstance[baseInstance.base_instance_id] || []
    },

    toggleDeliverable(baseInstance) {
      const baseInstanceId = baseInstance.base_instance_id
      const expanded = !this.expandedDeliverables[baseInstanceId]

      this.$set(this.expandedDeliverables, baseInstanceId, expanded)

      if (expanded) {
        this.fetchDeliverableAssets(baseInstance)
      }
    },

    toggleAll() {
      const expand = !this.allDeliverablesExpanded

      this.baseInstances.forEach(base => {
        this.$set(this.expandedDeliverables, base.base_instance_id, expand)

        if (expand) {
          this.fetchDeliverableAssets(base)
        }
      })
    },

    async expandAllDeliverables() {
      this.baseInstances.forEach(base => {
        this.$set(this.expandedDeliverables, base.base_instance_id, true)
      })

      await Promise.all(this.baseInstances.map(base => this.fetchDeliverableAssets(base)))
    },

    /**
     * Loads the assets of every format instance of the deliverable and
     * groups them all together under the deliverable itself.
     */
    async fetchDeliverableAssets(baseInstance, force = false) {
      const baseInstanceId = baseInstance.base_instance_id

      if (!force && this.assetsByBaseInstance[baseInstanceId]) {
        return
      }

      const formatInstances = (baseInstance.formatInstances || []).filter(
        format => format?.job_deliver?.job_deliver_id
      )

      if (!formatInstances.length) {
        this.$set(this.assetsByBaseInstance, baseInstanceId, [])
        return
      }

      this.$set(this.loadingAssets, baseInstanceId, true)

      try {
        const responses = await Promise.all(
          formatInstances.map(async formatInstance => {
            try {
              const response = await JobDeliverAssetAPI.readAssets(
                formatInstance.job_deliver.job_deliver_id,
                this.effectiveJobId,
                { 'filter[status]': 'pending,rejected' }
              )

              return {
                formatInstance,
                data: response.data
              }
            } catch (error) {
              console.error('Error loading assets:', error)
              return { formatInstance, data: null }
            }
          })
        )

        const assets = responses.reduce((all, { formatInstance, data }) => {
          return all.concat(this.buildAssets(data, formatInstance))
        }, [])

        this.$set(this.assetsByBaseInstance, baseInstanceId, assets)

        assets.forEach(asset => this.detectAssetAspectRatio(this.getDisplayedThumbnail(asset)))
      } finally {
        this.$set(this.loadingAssets, baseInstanceId, false)
      }
    },

    /**
     * Loads a thumbnail image off-screen just to read its natural
     * dimensions, since the API doesn't expose width/height for renditions.
     * The resulting "width / height" ratio is cached (keyed by thumbnail URL,
     * since each version has its own image) and used to size the thumbnail
     * card so it matches the image's real aspect ratio.
     */
    detectAssetAspectRatio(thumbnailUrl) {
      if (!thumbnailUrl) return
      if (this.assetAspectRatios[thumbnailUrl]) return

      const img = new Image()
      img.onload = () => {
        if (img.naturalWidth && img.naturalHeight) {
          this.$set(this.assetAspectRatios, thumbnailUrl, img.naturalWidth / img.naturalHeight)
        }
      }
      img.onerror = () => {
        // Keep the default square ratio when the image can't be measured
      }
      img.src = thumbnailUrl
    },

    getAssetAspectRatio(asset) {
      const thumbnailUrl = this.getDisplayedThumbnail(asset)
      if (!thumbnailUrl) return 1

      return this.assetAspectRatios[thumbnailUrl] || 1
    },

    /**
     * Normalizes the assets-read payload into asset cards.
     */
    buildAssets(assetData, formatInstance) {
      const jobDeliverAssets = this.extractJobDeliverAssets(assetData)

      return jobDeliverAssets
        .map(item => {
          const markupSession = item.markup_session

          if (!markupSession) return null

          const markupSessionNodes = markupSession.markup_session_node || []
          const latestNode = this.getLatestVersionNode(markupSessionNodes)

          return {
            job_deliver_asset_id: item.job_deliver_asset_id,
            markup_session_id: markupSession.markup_session_id || markupSession.id,
            name: markupSession.name || this.$t('markup.unnamed_asset'),
            status: latestNode ? this.determineNodeStatus(latestNode) : 'pending',
            thumbnail: latestNode ? this.getThumbnailFromNode(latestNode) : null,
            latestNode,
            allNodes: markupSessionNodes,
            currentVersion: latestNode ? latestNode.version : 1,
            formatLabel: this.getFormatLabel(formatInstance)
          }
        })
        .filter(asset => asset !== null)
    },

    extractJobDeliverAssets(assetData) {
      if (!assetData) return []

      if (Array.isArray(assetData)) {
        return assetData
      }

      if (assetData.job_deliver_asset) {
        return Array.isArray(assetData.job_deliver_asset)
          ? assetData.job_deliver_asset
          : [assetData.job_deliver_asset]
      }

      return []
    },

    getFormatLabel(formatInstance) {
      return formatInstance?.format_desc || formatInstance?.format_title || ''
    },

    getLatestVersionNode(markupSessionNodes) {
      if (!markupSessionNodes || markupSessionNodes.length === 0) {
        return null
      }

      return markupSessionNodes.reduce((latest, current) => {
        if (!latest || current.version > latest.version) {
          return current
        }
        return latest
      }, null)
    },

    getThumbnailFromNode(node) {
      if (!node || !node.renditions || node.renditions.length === 0) {
        return null
      }

      const thumbRenditions = node.renditions.filter(r => r.type === 'thumb')
      const previewRenditions = node.renditions.filter(r => r.type === 'preview')
      const renditionsToUse = thumbRenditions.length > 0 ? thumbRenditions : previewRenditions

      if (renditionsToUse.length === 0) {
        return null
      }

      const lastRendition = renditionsToUse.reduce((last, current) => {
        return parseInt(current.order, 10) > parseInt(last.order, 10) ? current : last
      }, renditionsToUse[0])

      return lastRendition.url
    },

    /**
     * Returns the nodes of an asset sorted by version, ascending.
     */
    getSortedNodes(asset) {
      return [...(asset.allNodes || [])].sort((a, b) => a.version - b.version)
    },

    /**
     * Returns the node currently selected for display on the asset card.
     * Defaults to the latest version when nothing has been selected yet.
     */
    getSelectedNode(asset) {
      const nodes = this.getSortedNodes(asset)
      if (nodes.length === 0) return null

      const selectedNodeId = this.selectedVersionByAsset[asset.job_deliver_asset_id]
      const selectedNode = nodes.find(node => node.node_id === selectedNodeId)

      return selectedNode || asset.latestNode || nodes[nodes.length - 1]
    },

    getDisplayedThumbnail(asset) {
      const node = this.getSelectedNode(asset)
      return node ? this.getThumbnailFromNode(node) : asset.thumbnail
    },

    getDisplayedStatus(asset) {
      const node = this.getSelectedNode(asset)
      return node ? this.determineNodeStatus(node) : asset.status
    },

    getDisplayedVersionNumber(asset) {
      const node = this.getSelectedNode(asset)
      return node ? node.version : asset.currentVersion
    },

    /**
     * Returns a window of nodes to render as chips, keeping the selected
     * version visible while capping the amount shown at once so the row
     * fits within the asset card width.
     */
    getVisibleVersionNodes(asset, maxVisible = 4) {
      const nodes = this.getSortedNodes(asset)
      if (nodes.length <= maxVisible) return nodes

      const selectedNode = this.getSelectedNode(asset)
      const selectedIndex = nodes.findIndex(node => node.node_id === selectedNode?.node_id)

      // Center the window around the selected version, clamped to the array bounds
      let start = selectedIndex - Math.floor(maxVisible / 2)
      start = Math.max(0, Math.min(start, nodes.length - maxVisible))

      return nodes.slice(start, start + maxVisible)
    },

    selectVersion(asset, node) {
      this.$set(this.selectedVersionByAsset, asset.job_deliver_asset_id, node.node_id)
      this.detectAssetAspectRatio(this.getDisplayedThumbnail(asset))
    },

    canGoToPreviousVersion(asset) {
      const nodes = this.getSortedNodes(asset)
      const currentNode = this.getSelectedNode(asset)
      const currentIndex = nodes.findIndex(node => node.node_id === currentNode?.node_id)
      return currentIndex > 0
    },

    canGoToNextVersion(asset) {
      const nodes = this.getSortedNodes(asset)
      const currentNode = this.getSelectedNode(asset)
      const currentIndex = nodes.findIndex(node => node.node_id === currentNode?.node_id)
      return currentIndex !== -1 && currentIndex < nodes.length - 1
    },

    goToPreviousVersion(asset) {
      if (!this.canGoToPreviousVersion(asset)) return

      const nodes = this.getSortedNodes(asset)
      const currentNode = this.getSelectedNode(asset)
      const currentIndex = nodes.findIndex(node => node.node_id === currentNode?.node_id)

      this.$set(this.selectedVersionByAsset, asset.job_deliver_asset_id, nodes[currentIndex - 1].node_id)
      this.detectAssetAspectRatio(this.getDisplayedThumbnail(asset))
    },

    goToNextVersion(asset) {
      if (!this.canGoToNextVersion(asset)) return

      const nodes = this.getSortedNodes(asset)
      const currentNode = this.getSelectedNode(asset)
      const currentIndex = nodes.findIndex(node => node.node_id === currentNode?.node_id)

      this.$set(this.selectedVersionByAsset, asset.job_deliver_asset_id, nodes[currentIndex + 1].node_id)
      this.detectAssetAspectRatio(this.getDisplayedThumbnail(asset))
    },

    determineNodeStatus(node) {
      if (node.status === 'approved' || node.approval_status === 'approved') {
        return 'approved'
      } else if (node.status === 'rejected' || node.approval_status === 'rejected') {
        return 'rejected'
      }
      return 'pending'
    },

    getStatusTitle(statusId) {
      const status = this.statusOptions.find(s => s.status_id === statusId)
      return status ? status.status_title : this.$t('draft')
    },

    getBaseInstanceIcon(baseInstance) {
      const deliverable = this.deliverableOptions.find(d => d.deliverable_id === baseInstance.deliverable_id)
      return deliverable?.icon || 'mdi-package-variant'
    },

    openAssetReview(asset) {
      this.selectedAssetForReview = asset
      this.markupSessionDialog = true
    },

    closeMarkupSessionDialog() {
      this.markupSessionDialog = false
      this.selectedAssetForReview = null
    },

    onMarkupDialogChange(isOpen) {
      if (!isOpen) {
        this.selectedAssetForReview = null
      }
    },

    handleStatusUpdate(statusData) {
      // Update the status of the reviewed asset without refetching everything
      Object.keys(this.assetsByBaseInstance).forEach(baseInstanceId => {
        const assets = this.assetsByBaseInstance[baseInstanceId] || []

        const asset = assets.find(a => a.markup_session_id === statusData.sessionId) ||
          assets.find(a => a.latestNode && a.latestNode.node_id === statusData.nodeId)

        if (asset) {
          this.$set(asset, 'status', statusData.status)
        }
      })
    },

    downloadAsset(asset) {
      const node = this.getSelectedNode(asset) || asset.latestNode

      if (!node || !node.node_id) {
        this[AppActions.OPEN_APP_ERROR_MESSAGE](this.$t('markup.error_downloading_asset'))
        return
      }

      this[ContentActions.DOWNLOAD_FILE]({
        nodeId: node.node_id,
        control: { downloading: false }
      })
    }
  }
}
</script>

<style scoped lang="scss">
.custom-vertical-divider {
  border-width: 0 2px 0 0;
  max-height: 32px;
  align-self: center;
}

.deliverable-item {
  background-color: #f2f3f5;
}

.assets-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}

.asset-card {
  display: flex;
  flex-direction: column;

  &-inner {
    cursor: pointer;
    transition: all 0.2s;
    position: relative;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15) !important;

      .asset-actions {
        opacity: 1;
      }
    }
  }
}

.asset-thumbnail {
  position: relative;
  width: 100%;
  /* Falls back to a square while the real aspect ratio hasn't been detected yet */
  aspect-ratio: 1 / 1;
  overflow: hidden;
  background-color: #f5f5f5;
  border-radius: 4px;

  .v-image {
    width: 100%;
    height: 100%;
  }

  &-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.asset-status-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
}

.asset-version-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: nowrap;

  .v-chip {
    cursor: pointer;
  }
}

.asset-actions {
  position: absolute;
  top: 6px;
  right: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}

.asset-title {
  text-align: center;
  line-height: 1.2;
}
</style>
